import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../i18n";

type Pref = {
  key: string;
  enabled: boolean;
  label: string;
  description: string;
  scope: string;
};

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "0.65rem 1rem",
      borderRadius: 12,
      background: "var(--clr-surface)",
      border: "1px solid var(--clr-border)",
      backdropFilter: "var(--clr-blur)",
      WebkitBackdropFilter: "var(--clr-blur)",
      boxShadow: "var(--clr-shadow-sm)",
      color: "var(--clr-text2)",
      fontSize: "0.85rem",
      fontWeight: 500,
      marginTop: "1rem",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 4, background: ok ? "#22c55e" : "#ef4444", display: "inline-block", flexShrink: 0 }} />
      {msg}
    </div>
  );
}

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      style={{
        width: 44, height: 24, padding: 0, border: "none",
        borderRadius: 12, background: on ? "var(--clr-org-primary)" : "rgba(12,74,94,0.18)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        position: "relative", transition: "background .15s",
      }}
      aria-pressed={on}
    >
      <span style={{
        position: "absolute", top: 2, left: on ? 22 : 2,
        width: 20, height: 20, borderRadius: "50%",
        background: "#fff", transition: "left .15s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }} />
    </button>
  );
}

/** Toggles de preferencias de email del usuario (GET/PUT /auth/me/notification-prefs).
 *  Se usa dentro de la página Alertas (tab "Preferencias de email") y en la
 *  página /notifications/preferences que conserva el superadmin. */
export default function EmailPreferences() {
  const t = useT();
  const [prefs, setPrefs] = useState<Pref[] | null>(null);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<{ prefs: Pref[] }>("/auth/me/notification-prefs");
        setPrefs(data.prefs);
      } catch (e) {
        setError(e instanceof Error ? e.message : t("alerts.prefsLoadError"));
      }
    })();
  }, []);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }

  function toggle(key: string) {
    if (!prefs) return;
    setPrefs(prefs.map(p => p.key === key ? { ...p, enabled: !p.enabled } : p));
    setDirty(d => ({ ...d, [key]: true }));
  }

  async function save() {
    if (!prefs) return;
    setLoading(true);
    try {
      const body = { prefs: Object.fromEntries(prefs.filter(p => dirty[p.key]).map(p => [p.key, p.enabled])) };
      await api.put("/auth/me/notification-prefs", body);
      setDirty({});
      showToast(t("alerts.prefsSaved"), true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("alerts.prefsSaveError"), false);
    } finally {
      setLoading(false);
    }
  }

  const hasChanges = Object.values(dirty).some(Boolean);
  // Avisos por severidad (feedback de Carlos, 20 oct 2026): niveles A/D/I de la tabla de
  // severidad de la organización × tu portfolio / tu competencia.
  const sevPrefs = (prefs ?? []).filter(p => p.key.startsWith("sev_"));
  const otherPrefs = (prefs ?? []).filter(p => !p.key.startsWith("sev_"));
  const sevOn = (side: string, lvl: string) => sevPrefs.find(p => p.key === `sev_${side}_${lvl}`)?.enabled ?? false;

  return (
    <div>
      <p style={{ color: "var(--clr-text3)", fontSize: "0.85rem", margin: "0 0 1.25rem" }}>
        {t("alerts.prefsIntro")}
      </p>

      {error && <Toast msg={error} ok={false} />}
      {!prefs && !error && (
        <p style={{ color: "var(--clr-text3)" }}>{t("alerts.loading")}</p>
      )}

      {prefs && prefs.length === 0 && (
        <p style={{ color: "var(--clr-text3)" }}>{t("alerts.noPrefs")}</p>
      )}

      {sevPrefs.length > 0 && (
        <div style={{ background: "var(--clr-card)", border: "1px solid var(--clr-border)", borderRadius: 14, padding: "18px 20px", marginBottom: 16 }}>
          <strong style={{ color: "var(--clr-text)", fontSize: "0.97rem" }}>{t("alerts.sevTitle")}</strong>
          <p style={{ margin: "4px 0 12px", color: "var(--clr-text3)", fontSize: "0.83rem", lineHeight: 1.5 }}>
            {t("alerts.sevIntro")} <Link to="/severity" style={{ color: "var(--clr-org-primary)", fontWeight: 600 }}>{t("alerts.sevLink")}</Link>
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: "0.85rem", minWidth: 420 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px 12px 6px 0", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.7, color: "var(--clr-text4)" }}>{t("alerts.sevLevel")}</th>
                  {(["portfolio", "competitor"] as const).map(side => (
                    <th key={side} style={{ padding: "6px 14px", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.7, color: "var(--clr-text4)" }}>
                      {t(side === "portfolio" ? "alerts.sevOwn" : "alerts.sevCompetitor")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(["A", "D", "I"] as const).map(lvl => (
                  <tr key={lvl} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                    <td style={{ padding: "9px 12px 9px 0", color: "var(--clr-text)" }}>{t(`alerts.lvl${lvl}`)}</td>
                    {(["portfolio", "competitor"] as const).map(side => (
                      <td key={side} style={{ padding: "9px 14px", textAlign: "center" }}>
                        <input type="checkbox" checked={sevOn(side, lvl)} onChange={() => toggle(`sev_${side}_${lvl}`)}
                          aria-label={`${t(`alerts.lvl${lvl}`)} · ${t(side === "portfolio" ? "alerts.sevOwn" : "alerts.sevCompetitor")}`}
                          style={{ width: 17, height: 17, accentColor: "var(--clr-org-primary)", cursor: "pointer" }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ margin: "10px 0 0", color: "var(--clr-text4)", fontSize: "0.75rem", lineHeight: 1.45 }}>{t("alerts.sevNote")}</p>
        </div>
      )}

      {otherPrefs.length > 0 && (
        <div style={{ background: "var(--clr-card)", border: "1px solid var(--clr-border)", borderRadius: 14 }}>
          {otherPrefs.map((p, i) => (
            <div
              key={p.key}
              style={{
                display: "flex", alignItems: "flex-start", gap: 16,
                padding: "18px 20px",
                borderTop: i === 0 ? "none" : "1px solid var(--clr-border)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--clr-text)", fontSize: "0.97rem" }}>{p.label}</strong>
                  {p.scope && (
                    <span style={{
                      fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.5px",
                      background: "rgba(8,145,178,0.10)", color: "#0891b2",
                      padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                    }}>{p.scope}</span>
                  )}
                </div>
                <p style={{ margin: "4px 0 0", color: "var(--clr-text3)", fontSize: "0.83rem", lineHeight: 1.5 }}>
                  {p.description}
                </p>
              </div>
              <div style={{ paddingTop: 4 }}>
                <Toggle on={p.enabled} onChange={() => toggle(p.key)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {prefs && prefs.length > 0 && (
        <div style={{ marginTop: "1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={save}
            disabled={!hasChanges || loading}
            style={{
              padding: "10px 22px", borderRadius: 10, border: "none",
              background: hasChanges ? "#0891b2" : "var(--clr-surface)",
              color: hasChanges ? "#fff" : "var(--clr-text3)",
              fontSize: "0.9rem", fontWeight: 600,
              cursor: hasChanges && !loading ? "pointer" : "not-allowed",
              transition: "all .15s",
            }}
          >
            {loading ? t("alerts.saving") : t("alerts.saveChanges")}
          </button>
        </div>
      )}

      {toast && <Toast {...toast} />}
    </div>
  );
}
